import {DateTime} from "luxon";
import {useCallback, useEffect, useState} from "react";
import { Button, DatePicker, Modal, LegacyStack, Text } from "@shopify/polaris";

export function formatDateToUsFormat(dateString)
{
    return DateTime
        .fromISO(dateString, {
            zone: window.shopifyTimeZone,
        })
        .setLocale('en-US')
        .toLocaleString({
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            timeZoneName: 'short',
        });
}

export function toDate(input, timezone = true)
{
    if (!input) {
        return '';
    }

    const date = typeof input === 'string'
        ? DateTime.fromJSDate(new Date(input))
        : DateTime.fromJSDate(input);

    if (timezone) {
        return date
            .setLocale('en-US')
            .toLocaleString(DateTime.DATE_SHORT);
    }

    return date
        .setLocale('en-US')
        .toLocaleString(DateTime.DATE_SHORT);
}

export function formatDate(dateString)
{
    return DateTime.fromJSDate(new Date(dateString)).toISODate();
}

export function DatePickerModal({
    startDate,
    endDate,
    onChange,
    buttonLabel,
    modalTitle,
    allowRange = false,
    multiMonth = false,
    disabled = false,
})
{
    const date = startDate ? new Date(startDate) : new Date();

    const [{month, year}, setDate] = useState({month: date.getMonth(), year: date.getFullYear()});
    const [selectedDates, setSelectedDates] = useState({
        start: new Date(date),
        end: new Date(endDate ?? date),
    });
    const [modalActive, setModalActive] = useState(false)

    useEffect(() => {
        const tempDate = startDate ? new Date(startDate) : new Date();

        setSelectedDates({
            start: tempDate,
            end: new Date(endDate ?? tempDate),
        });
        setDate({month: tempDate.getMonth(), year: tempDate.getFullYear()});
    }, [startDate, endDate]);

    const handleMonthChange = useCallback(
        (month, year) => setDate({month, year}),
        [],
    );

    const submit = useCallback(() => {
        onChange(allowRange
            ? selectedDates
            : selectedDates.start
        );

        setModalActive(false);
    }, [selectedDates])

    return <>
        <LegacyStack vertical spacing={'loose'}>
            <Text variant="bodyMd" as="span">
                {startDate ? toDate(selectedDates.start) : ''}
                {endDate && allowRange ? ('-' + toDate(selectedDates.end)) : ''}
            </Text>
            <Button disabled={disabled} size={'slim'} onClick={() => setModalActive(true)}>{buttonLabel ?? 'Select Date'}</Button>
        </LegacyStack>
        <Modal
            open={modalActive}
            onClose={() => setModalActive(false)}
            title={modalTitle ?? 'Select Date'}
            primaryAction={{
                content: 'Submit',
                onAction: submit,
            }}
            secondaryActions={[
                {
                    content: 'Cancel',
                    onAction: () => setModalActive(false),
                },
            ]}
        >
            <Modal.Section>
                <DatePicker
                    month={month}
                    year={year}
                    onChange={setSelectedDates}
                    onMonthChange={handleMonthChange}
                    selected={selectedDates}
                    allowRange={allowRange}
                    multiMonth={multiMonth}
                />
            </Modal.Section>
        </Modal>
    </>;
}
